import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Filter,
  MapPin,
  Users,
  GraduationCap,
  Crown,
  Briefcase,
  BarChart3,
  Banknote,
  Megaphone,
  Cpu,
  Search,
  ExternalLink,
  Clock3,
  Info,
  LayoutGrid,
  List,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  X
} from 'lucide-react';
import CustomDropdown from './CustomDropdown';
import { useApiClient } from '../auth/useApiClient';

const DISCIPLINE_VISUALS = {
  leadership: { icon: Crown, key: 'leadership' },
  'general-management': { icon: Briefcase, key: 'general-management' },
  strategy: { icon: BarChart3, key: 'strategy' },
  finance: { icon: Banknote, key: 'finance' },
  marketing: { icon: Megaphone, key: 'marketing' },
  'digital-transformation-and-innovation': { icon: Cpu, key: 'digital' },
  'human-resources': { icon: Users, key: 'hr' }
};

const normalizeDisciplineKey = (discipline = '') =>
  discipline.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const normalizeSearchText = (value = '') =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const tokenizeSearchText = (value = '') =>
  normalizeSearchText(value)
    .split(' ')
    .filter(Boolean);

const splitPersonNames = (value = '') =>
  String(value ?? '')
    .split(/\s*(?:,|\/|;|\+|&)\s*/)
    .map((name) => name.trim())
    .filter(Boolean);

const isLikelyPersonName = (value = '') => {
  const normalized = normalizeSearchText(value);
  if (!normalized) return false;
  if (/\bfaculty\b|\bteam\b|\bstaff\b|\bparticipants\b/.test(normalized)) return false;
  return normalized.split(' ').length >= 2;
};

const scoreSearchField = (
  fieldValue,
  queryTokens,
  {
    allowSubstring = false,
    minSubstringLength = 4,
    exactTokenScore = 12,
    prefixScore = 10,
    substringScore = 7,
    phraseScore = 6
  } = {}
) => {
  const normalizedField = normalizeSearchText(fieldValue);
  const fieldTokens = tokenizeSearchText(fieldValue);
  if (!normalizedField || !fieldTokens.length) {
    return 0;
  }

  let score = 0;

  for (const queryToken of queryTokens) {
    if (!queryToken) continue;

    let bestTokenScore = 0;

    for (const fieldToken of fieldTokens) {
      if (fieldToken === queryToken) {
        bestTokenScore = Math.max(bestTokenScore, exactTokenScore);
      } else if (fieldToken.startsWith(queryToken)) {
        bestTokenScore = Math.max(bestTokenScore, prefixScore);
      } else if (allowSubstring && queryToken.length >= minSubstringLength && fieldToken.includes(queryToken)) {
        bestTokenScore = Math.max(bestTokenScore, substringScore);
      }
    }

    if (!bestTokenScore) {
      return 0;
    }

    score += bestTokenScore;
  }

  const normalizedQuery = queryTokens.join(' ');
  if (normalizedQuery && normalizedField.includes(normalizedQuery)) {
    score += phraseScore;
  }

  return score;
};

const getCourseSearchScore = (course, queryTokens) => {
  if (!queryTokens.length) {
    return 0;
  }

  const titleFields = [
    course.title,
    course.programmeCode,
    course.discipline,
    course.format,
    course.location
  ];

  const facultyFields = [
    course.instructor,
    course.facultyBio,
    course.keyDetails?.programmeDirector,
    course.keyDetails?.academicDirector
  ];

  const audienceFields = [
    course.targetAudience,
    ...(course.keyTopics || [])
  ];

  const narrativeFields = [
    course.description,
    ...((course.testimonials || []).slice(0, 3).flatMap((item) => {
      if (typeof item === 'string') return [item];
      if (item && typeof item === 'object') return [item.quote, item.name, item.designation].filter(Boolean);
      return [];
    }))
  ];

  const bestTitleScore = Math.max(
    0,
    ...titleFields.map((field) => scoreSearchField(field, queryTokens, { phraseScore: 8 }))
  );
  const bestFacultyScore = Math.max(
    0,
    ...facultyFields.map((field) =>
      scoreSearchField(field, queryTokens, {
        allowSubstring: true,
        minSubstringLength: 3,
        exactTokenScore: 18,
        prefixScore: 16,
        substringScore: 13,
        phraseScore: 10
      })
    )
  );
  const bestAudienceScore = Math.max(
    0,
    ...audienceFields.map((field) => scoreSearchField(field, queryTokens, { phraseScore: 5 }))
  );
  const bestNarrativeScore = queryTokens.every((token) => token.length >= 4)
    ? Math.max(
      0,
      ...narrativeFields.map((field) =>
        scoreSearchField(field, queryTokens, {
          allowSubstring: true,
          exactTokenScore: 7,
          prefixScore: 6,
          substringScore: 5,
          phraseScore: 4
        })
      )
    )
    : 0;

  return bestTitleScore + bestFacultyScore + bestAudienceScore + bestNarrativeScore;
};

const getDisciplineVisual = (discipline) => {
  const normalized = normalizeDisciplineKey(discipline);
  return DISCIPLINE_VISUALS[normalized] || { icon: GraduationCap, key: 'default' };
};

const DISCIPLINE_ACCENTS = {
  leadership: '#6f61b5',
  'general-management': '#2f79b7',
  strategy: '#008b8f',
  finance: '#ad584f',
  marketing: '#a34f82',
  digital: '#008b72',
  hr: '#b75a58',
  default: '#687386'
};

const getDisciplineAccent = (discipline) => {
  const visual = getDisciplineVisual(discipline);
  return DISCIPLINE_ACCENTS[visual.key] || DISCIPLINE_ACCENTS.default;
};

const cleanOverviewText = (value = '') =>
  (value || '')
    .replace(/â€™/g, "'")
    .replace(/â€“/g, '-')
    .replace(/â€˜/g, "'")
    .replace(/â€œ|â€\x9d|â€\x9c|â€\x9f/g, '"')
    .replace(/Â/g, ' ')
    .replace(/^[^\n]*Key Data\s*&\s*Testimonials\s*/i, '')
    .replace(/\b(top\s*5|average\s*age|average\s*work\s*experience|website|brochure)\b.*$/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

const hasCorruptedText = (value = '') =>
  /(â€™|â€|Ã|Â|\uFFFD)/.test(value || '');

const normaliseSentence = (value = '') => {
  const cleaned = cleanOverviewText(value)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

  if (!cleaned) return '';
  return /[.!?]$/.test(cleaned) ? cleaned : `${cleaned}.`;
};

const lowerCaseFirst = (value = '') =>
  value ? value.charAt(0).toLowerCase() + value.slice(1) : '';

const buildProgrammeOverview = (course, overview) => {
  const cleanedOverview = normaliseSentence(cleanOverviewText(overview?.summary || '')
    .replace(/â€™|’/g, "'")
    .replace(/â€˜|‘/g, "'")
    .replace(/â€œ|â€|“/g, '"')
    .replace(/â€|”/g, '"')
    .replace(/â€“|–|â€”|—/g, '-')
    .replace(/â€¦|…/g, '...')
    .replace(/Â/g, ' ')
    .replace(/\bE xplore\b/g, 'Explore'));

  if (cleanedOverview && !hasCorruptedText(cleanedOverview) && cleanedOverview.length >= 140) {
    return cleanedOverview;
  }

  const rewrittenDescription = [];

  if (course.description) {
    rewrittenDescription.push(normaliseSentence(course.description));
  }

  if (course.objectives) {
    const objectiveText = lowerCaseFirst(course.objectives);
    const audienceText = lowerCaseFirst(course.targetAudience || 'executives');
    rewrittenDescription.push(
      normaliseSentence(`Designed for ${audienceText}, it focuses on ${objectiveText}`)
    );
  }

  if (course.outcomes) {
    rewrittenDescription.push(normaliseSentence(course.outcomes));
  } else if (Array.isArray(course.keyTopics) && course.keyTopics.length > 0) {
    rewrittenDescription.push(
      normaliseSentence(`Participants explore themes such as ${course.keyTopics.slice(0, 3).join(', ')}`)
    );
  }

  return rewrittenDescription.filter(Boolean).join(' ');
};

const buildConciseProgrammeOverview = (course) => {
  const conciseDescription = [];

  if (course.description) {
    conciseDescription.push(normaliseSentence(course.description));
  }

  if (course.targetAudience && course.objectives) {
    conciseDescription.push(
      normaliseSentence(
        `Designed for ${lowerCaseFirst(course.targetAudience)}, it focuses on ${lowerCaseFirst(course.objectives)}`
      )
    );
  } else if (course.objectives) {
    conciseDescription.push(normaliseSentence(course.objectives));
  }

  if (course.outcomes) {
    conciseDescription.push(normaliseSentence(course.outcomes));
  } else if (Array.isArray(course.keyTopics) && course.keyTopics.length > 0) {
    conciseDescription.push(
      normaliseSentence(`Participants explore themes such as ${course.keyTopics.slice(0, 3).join(', ')}`)
    );
  }

  return conciseDescription
    .filter(Boolean)
    .slice(0, 3)
    .join(' ');
};

const PERSON_NAME_CORRECTIONS = new Map([
  ['Narayan Nalik', 'Narayan Naik']
]);

const cleanKeyMetricValue = (value = '') =>
  cleanOverviewText(value)
    .replace(/[–—]/g, '-')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();

const sanitizePersonName = (value = '') => {
  const cleaned = cleanKeyMetricValue(value);
  if (!cleaned || /^(tbc|n\/a|brochure|website)$/i.test(cleaned)) return null;

  const parts = cleaned
    .split(/\s*(?:\/|\+|;| - )\s*/)
    .map((part) => part.replace(/\([^)]*\)/g, '').trim())
    .filter(Boolean)
    .filter((part) => part.split(/\s+/).length >= 2);

  if (!parts.length) return null;
  return [...new Set(parts)]
    .map((part) => PERSON_NAME_CORRECTIONS.get(part) || part)
    .join(', ');
};

const sanitizeMetricValue = (value = '') => {
  const cleaned = cleanKeyMetricValue(value);
  if (!cleaned || /^(n\/a|null|undefined)$/i.test(cleaned)) return null;
  return cleaned;
};

const formatManagementExperience = (value = '') => {
  const cleaned = sanitizeMetricValue(value);
  if (!cleaned) return null;
  return /^\d+(?:\.\d+)?$/i.test(cleaned) ? `${cleaned} years` : cleaned;
};

const sanitizeKeyDetails = (keyDetails) => {
  if (!keyDetails || typeof keyDetails !== 'object') return null;

  const averageExperience = formatManagementExperience(keyDetails.averageExperience);
  const academicDirector = sanitizePersonName(keyDetails.academicDirector);
  const programmeDirector = sanitizePersonName(keyDetails.programmeDirector);

  if (!averageExperience && !academicDirector && !programmeDirector) {
    return null;
  }

  return {

    averageExperience,
    academicDirector,
    programmeDirector
  };
};

const sanitizeContentDetails = (contentDetails) => {
  if (!contentDetails || typeof contentDetails !== 'object') return null;
  const cleanedEntries = Object.entries(contentDetails)
    .map(([key, value]) => {
      if (typeof value !== 'string') return [key, ''];
      const cleaned = value.trim();
      return [key, /\[object Object\]/i.test(cleaned) ? '' : cleaned];
    })
    .filter(([, value]) => value);
  return cleanedEntries.length ? Object.fromEntries(cleanedEntries) : null;
};

const CATALOGUE_UPDATED_EVENT = 'execed:catalogue-updated';
const CATALOGUE_UPDATED_STORAGE_KEY = 'execed.catalogue.updatedAt';
const LOCATION_SORT_ORDER = ['london', 'dubai', 'riyadh'];

const escapeRegExp = (value = '') =>
  String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanTestimonialQuote = (quote = '', course = {}, rawKeyDetails = null) => {
  let cleaned = cleanOverviewText(quote);
  if (!cleaned) return '';

  const leadingBleedSegments = [
    rawKeyDetails?.academicDirector,
    rawKeyDetails?.programmeDirector,
    ...(Array.isArray(rawKeyDetails?.industries) ? rawKeyDetails.industries : []),
    ...(Array.isArray(rawKeyDetails?.nationalities) ? rawKeyDetails.nationalities : []),
    ...(Array.isArray(rawKeyDetails?.seniorities) ? rawKeyDetails.seniorities : [])
  ]
    .flatMap((value) => String(value || '').split(/\s*(?:\/|\+|;)\s*/))
    .map((value) => cleanOverviewText(value))
    .filter(Boolean);

  let removedLeadingSegment = true;
  while (removedLeadingSegment) {
    removedLeadingSegment = false;

    for (const segment of leadingBleedSegments) {
      const pattern = new RegExp(`^${escapeRegExp(segment)}\\s*`, 'i');
      if (pattern.test(cleaned)) {
        cleaned = cleaned.replace(pattern, '').trim();
        removedLeadingSegment = true;
      }
    }
  }

  if (course.programmeCode) {
    cleaned = cleaned.replace(new RegExp(`^${escapeRegExp(course.programmeCode)}\\s*`, 'i'), '').trim();
  }

  cleaned = cleaned.replace(/^(?:top\s*5|average\s*age|average\s*work\s*experience|website|brochure)\b.*$/i, '').trim();

  return cleaned;
};

const normalizeTestimonialEntry = (item, course = {}, rawKeyDetails = null) => {
  if (!item) return null;

  if (typeof item === 'string') {
    const quote = cleanTestimonialQuote(item, course, rawKeyDetails);
    return quote ? { quote, name: '', designation: '' } : null;
  }

  if (typeof item === 'object') {
    const quote = cleanTestimonialQuote(item.quote || item.text || '', course, rawKeyDetails);
    const name = cleanOverviewText(item.name || '');
    const designation = cleanOverviewText(item.designation || item.role || '');
    if (!quote) return null;
    return { quote, name, designation };
  }

  return null;
};

const dedupeTestimonials = (items = []) => {
  const byKey = new Map();
  items.filter(Boolean).forEach((entry) => {
    const key = `${entry.quote}__${entry.name}__${entry.designation}`;
    if (!byKey.has(key)) {
      byKey.set(key, entry);
    }
  });
  return [...byKey.values()];
};

const getCourseSearchKey = (course) => course?.id ?? course?.title ?? '';

const buildCoursePeople = (course) => {
  const people = [];
  const addPeople = (role, value) => {
    splitPersonNames(value)
      .filter(isLikelyPersonName)
      .forEach((name) => {
        people.push({
          name,
          normalizedName: normalizeSearchText(name),
          role
        });
      });
  };

  addPeople('faculty', course.instructor);
  addPeople('faculty', course.facultyBio);
  addPeople('programmeDirector', course.keyDetails?.programmeDirector);
  addPeople('academicDirector', course.keyDetails?.academicDirector);

  return people;
};

const doesCourseMatchPersonSearch = (course, personSearch) => {
  if (!personSearch) return false;
  return buildCoursePeople(course).some((person) =>
    person.role === personSearch.role && person.normalizedName === personSearch.normalizedName
  );
};

const PERSON_ROLE_LABELS = {
  academicDirector: 'Academic Director',
  programmeDirector: 'Programme Director',
  faculty: 'Faculty'
};

const buildProgrammeCodeSuggestions = (courses, queryTokens) => {
  if (!queryTokens.length) return [];

  return courses
    .map((course) => {
      const code = String(course.programmeCode || '').trim();
      if (!code) return null;

      const score = scoreSearchField(code, queryTokens, {
        allowSubstring: true,
        minSubstringLength: 2,
        exactTokenScore: 20,
        prefixScore: 18,
        substringScore: 14,
        phraseScore: 10
      });

      if (!score) return null;

      return {
        type: 'programmeCode',
        code,
        normalizedCode: normalizeSearchText(code),
        title: course.title,
        score
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.code.localeCompare(b.code);
    })
    .slice(0, 5);
};

const buildPersonSearchSuggestions = (courses, queryTokens) => {
  if (!queryTokens.length) return [];

  const suggestions = new Map();

  courses.forEach((course) => {
    buildCoursePeople(course).forEach((person) => {
      const score = scoreSearchField(person.name, queryTokens, {
        allowSubstring: true,
        minSubstringLength: 2,
        exactTokenScore: 18,
        prefixScore: 16,
        substringScore: 13,
        phraseScore: 10
      });

      if (!score) return;

      const key = `${person.role}::${person.normalizedName}`;
      const existing = suggestions.get(key);

      if (!existing) {
        suggestions.set(key, {
          ...person,
          score,
          courseCount: 1
        });
        return;
      }

      suggestions.set(key, {
        ...existing,
        score: Math.max(existing.score, score),
        courseCount: existing.courseCount + 1
      });
    });
  });

  return [...suggestions.values()]
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;

      const roleOrder = ['academicDirector', 'programmeDirector', 'faculty'];
      if (a.role !== b.role) {
        return roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role);
      }

      if (a.courseCount !== b.courseCount) return a.courseCount - b.courseCount;
      return a.name.localeCompare(b.name);
    })
    .slice(0, 5);
};

const CourseList = () => {
  const { request } = useApiClient();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [personSearch, setPersonSearch] = useState(null);
  const [programmeCodeSearch, setProgrammeCodeSearch] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState('discipline-asc');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [filters, setFilters] = useState({ discipline: '', format: '', certificationEligible: '' });
  const lastFocusedElementRef = useRef(null);
  const modalDialogRef = useRef(null);
  const searchInputRef = useRef(null);
  const modalTitleId = 'programme-details-title';
  const queryTokens = useMemo(() => tokenizeSearchText(query), [query]);
  const personSuggestions = useMemo(
    () => buildPersonSearchSuggestions(courses, queryTokens),
    [courses, queryTokens]
  );
  const programmeCodeSuggestions = useMemo(
    () => buildProgrammeCodeSuggestions(courses, queryTokens),
    [courses, queryTokens]
  );
  const autoPersonSearch = !personSearch && personSuggestions.length === 1 ? personSuggestions[0] : null;
  const autoProgrammeCodeSearch = !programmeCodeSearch && !personSearch && programmeCodeSuggestions.length === 1
    ? programmeCodeSuggestions[0]
    : null;
  const activeProgrammeCodeSearch = programmeCodeSearch || autoProgrammeCodeSearch;
  const activePersonSearch = personSearch || autoPersonSearch;
  const showPersonSuggestions =
    isSearchFocused
    && queryTokens.length > 0
    && (personSuggestions.length > 0 || programmeCodeSuggestions.length > 0)
    && !personSearch
    && !programmeCodeSearch;

  const loadCourses = useCallback(() => {
    setLoading(true);
    return request('/api/courses')
      .then(response => response.json())
      .then(data => {
        const mergedCourses = data.map((course) => {
          const programmeOverview = buildConciseProgrammeOverview(course) || buildProgrammeOverview(course);
          const cleanedTestimonials = Array.isArray(course.testimonials)
            ? dedupeTestimonials(course.testimonials.map((item) => normalizeTestimonialEntry(item, course, course.keyDetails)))
            : [];
          const keyDetails = sanitizeKeyDetails(course.keyDetails);
          const contentDetails = sanitizeContentDetails(course.contentDetails);
          return {
            ...course,
            description: programmeOverview,
            testimonials: cleanedTestimonials,
            keyDetails,
            contentDetails
          };
        });

        setCourses(mergedCourses);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [request]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    const handleCatalogueUpdated = () => {
      loadCourses();
    };
    const handleStorageUpdated = (event) => {
      if (event.key === CATALOGUE_UPDATED_STORAGE_KEY) {
        loadCourses();
      }
    };

    window.addEventListener(CATALOGUE_UPDATED_EVENT, handleCatalogueUpdated);
    window.addEventListener('storage', handleStorageUpdated);

    return () => {
      window.removeEventListener(CATALOGUE_UPDATED_EVENT, handleCatalogueUpdated);
      window.removeEventListener('storage', handleStorageUpdated);
    };
  }, [loadCourses]);

  const filteredCourses = useMemo(() => {
    let filtered = [...courses];

    const searchScores = new Map();
    if (activeProgrammeCodeSearch) {
      filtered = filtered.filter((course) =>
        normalizeSearchText(course.programmeCode) === activeProgrammeCodeSearch.normalizedCode
      );
    } else if (activePersonSearch) {
      filtered = filtered.filter((course) => doesCourseMatchPersonSearch(course, activePersonSearch));
    } else if (queryTokens.length > 0) {
      filtered = filtered.filter((course) => {
        const score = getCourseSearchScore(course, queryTokens);
        searchScores.set(getCourseSearchKey(course), score);
        return score > 0;
      });
    }

    if (filters.discipline) {
      filtered = filtered.filter(c => c.discipline === filters.discipline);
    }
    if (filters.format) {
      filtered = filtered.filter(c => c.format === filters.format);
    }
    if (filters.certificationEligible === 'yes') {
      filtered = filtered.filter(c => c.certificationEligible === true);
    }

    const getDurationScore = (duration = '') => {
      const matches = [...duration.toLowerCase().matchAll(/(\d+(?:\.\d+)?)\s*(day|week|month|hour)s?/g)];
      if (!matches.length) return Number.MAX_SAFE_INTEGER;

      return matches.reduce((total, match) => {
        const amount = Number(match[1]);
        const unit = match[2];
        if (unit === 'month') return total + amount * 30;
        if (unit === 'week') return total + amount * 7;
        if (unit === 'hour') return total + amount / 8;
        return total + amount;
      }, 0);
    };

    const getCourseLocation = (course = {}) => {
      if (course.location) return String(course.location);
      return '';
    };

    const getLocationRank = (course = {}, reverse = false) => {
      const location = getCourseLocation(course).toLowerCase();
      const rank = LOCATION_SORT_ORDER.findIndex((item) => location.includes(item));
      if (rank < 0) return Number.MAX_SAFE_INTEGER;
      return reverse ? LOCATION_SORT_ORDER.length - 1 - rank : rank;
    };

    const compareText = (a = '', b = '') => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
    const compareBySort = (a, b) => {
      switch (sortBy) {
        case 'discipline-asc':
          return compareText(a.discipline, b.discipline) || compareText(a.title, b.title);
        case 'discipline-desc':
          return compareText(b.discipline, a.discipline) || compareText(a.title, b.title);
        case 'code-asc':
          return compareText(a.programmeCode, b.programmeCode) || compareText(a.title, b.title);
        case 'code-desc':
          return compareText(b.programmeCode, a.programmeCode) || compareText(a.title, b.title);
        case 'title-asc':
          return compareText(a.title, b.title);
        case 'title-desc':
          return compareText(b.title, a.title);
        case 'faculty-asc':
          return compareText(a.instructor, b.instructor) || compareText(a.title, b.title);
        case 'faculty-desc':
          return compareText(b.instructor, a.instructor) || compareText(a.title, b.title);
        case 'format-asc':
          return compareText(a.format, b.format) || compareText(a.title, b.title);
        case 'format-desc':
          return compareText(b.format, a.format) || compareText(a.title, b.title);
        case 'location-custom':
          return getLocationRank(a) - getLocationRank(b) || compareText(getCourseLocation(a), getCourseLocation(b)) || compareText(a.title, b.title);
        case 'location-desc':
          return getLocationRank(a, true) - getLocationRank(b, true) || compareText(getCourseLocation(a), getCourseLocation(b)) || compareText(a.title, b.title);
        case 'duration-short':
          return getDurationScore(a.duration) - getDurationScore(b.duration) || compareText(a.title, b.title);
        case 'duration-long':
          return getDurationScore(b.duration) - getDurationScore(a.duration) || compareText(a.title, b.title);
        default:
          return compareText(a.discipline, b.discipline) || compareText(a.title, b.title);
      }
    };

    if (!activeProgrammeCodeSearch && !activePersonSearch && queryTokens.length > 0) {
      filtered.sort((a, b) => {
        const scoreDifference =
          (searchScores.get(getCourseSearchKey(b)) || 0) - (searchScores.get(getCourseSearchKey(a)) || 0);
        if (scoreDifference !== 0) {
          return scoreDifference;
        }

        return compareBySort(a, b);
      });
    } else {
      filtered.sort(compareBySort);
    }

    return filtered;
  }, [activePersonSearch, activeProgrammeCodeSearch, courses, filters, queryTokens, sortBy]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ discipline: '', format: '', certificationEligible: '' });
    setQuery('');
    setPersonSearch(null);
    setProgrammeCodeSearch(null);
    setSortBy('discipline-asc');
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setPersonSearch((currentSelection) => {
      if (!currentSelection) return null;
      return normalizeSearchText(value) === currentSelection.normalizedName ? currentSelection : null;
    });
    setProgrammeCodeSearch((currentSelection) => {
      if (!currentSelection) return null;
      return normalizeSearchText(value) === currentSelection.normalizedCode ? currentSelection : null;
    });
  };

  const handlePersonSuggestionSelect = (suggestion) => {
    setQuery(suggestion.name);
    setPersonSearch(suggestion);
    setProgrammeCodeSearch(null);
    setIsSearchFocused(false);
  };

  const handleProgrammeCodeSuggestionSelect = (suggestion) => {
    setQuery(suggestion.code);
    setProgrammeCodeSearch(suggestion);
    setPersonSearch(null);
    setIsSearchFocused(false);
  };

  const openCourseModal = (course) => {
    lastFocusedElementRef.current = document.activeElement;
    setSelectedCourse(course);
  };

  const closeCourseModal = () => {
    setSelectedCourse(null);
  };

  useEffect(() => {
    if (!selectedCourse) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedCourse(null);
      }

      if (event.key === 'Tab') {
        const focusable = modalDialogRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => modalDialogRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      lastFocusedElementRef.current?.focus?.();
    };
  }, [selectedCourse]);

  const disciplines = [...new Set(courses.map(c => c.discipline))].sort();
  const hasNonEligibleCourses = courses.some((course) => course.certificationEligible !== true);
  const hasActiveFilters = Boolean(query.trim() || activePersonSearch || activeProgrammeCodeSearch || filters.discipline || filters.format || filters.certificationEligible);

  const buildCardSummary = (course) => {
    if (Array.isArray(course.keyTopics) && course.keyTopics.length > 0) {
      return `Focus: ${course.keyTopics.slice(0, 3).join(', ')}`;
    }
    if (course.targetAudience) return `Best for ${lowerCaseFirst(course.targetAudience)}`;
    if (course.outcomes) return course.outcomes;
    return course.description;
  };

  const listSortConfig = {
    discipline: ['discipline-asc', 'discipline-desc'],
    code: ['code-asc', 'code-desc'],
    title: ['title-asc', 'title-desc'],
    faculty: ['faculty-asc', 'faculty-desc'],
    format: ['format-asc', 'format-desc'],
    location: ['location-custom', 'location-desc'],
    duration: ['duration-short', 'duration-long'],
  };

  const handleListHeaderSort = (key) => {
    const [ascending, descending] = listSortConfig[key] || [];
    if (!ascending || !descending) return;
    setSortBy((current) => (current === ascending ? descending : ascending));
  };

  const renderListHeader = (key, label, className = '') => {
    const [ascending, descending] = listSortConfig[key] || [];
    const isActive = sortBy === ascending || sortBy === descending;
    const direction = sortBy === descending ? 'desc' : 'asc';
    const SortIcon = isActive ? (direction === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
      <button
        type="button"
        className={`programme-list-head-cell ${className} ${isActive ? 'active' : ''}`}
        onClick={() => handleListHeaderSort(key)}
        aria-label={`${label}: ${isActive ? `sorted ${direction === 'asc' ? 'ascending' : 'descending'}` : 'sort column'}`}
      >
        <span>{label}</span>
        <span className="programme-sort-indicator">
          <SortIcon size={13} aria-hidden="true" strokeWidth={2.2} />
        </span>
      </button>
    );
  };

  if (loading) return <section className="page"><p>Loading programmes...</p></section>;

  return (
    <section className="page page-programmes">
      <div className="catalogue-filter-shell">
        <div className="catalogue-filter-top">
          <div className="catalogue-search-field">
            <label className="input-label" htmlFor="programme-search">Search</label>
            <div className="catalog-search-wrap">
              <Search size={16} className="catalog-search-icon" />
              <input
                ref={searchInputRef}
                id="programme-search"
                type="text"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => {
                  window.setTimeout(() => setIsSearchFocused(false), 120);
                }}
                placeholder="Search by title, code, faculty, or topic"
                className="catalog-search-input"
                autoComplete="off"
              />
              {showPersonSuggestions && (
                <div className="catalog-search-suggestions" aria-label="Suggested people search">
                  {programmeCodeSuggestions.map((suggestion) => {
                    const isAutoSuggestion =
                      autoProgrammeCodeSearch?.normalizedCode === suggestion.normalizedCode;

                    return (
                      <button
                        key={`programme-code-${suggestion.normalizedCode}`}
                        type="button"
                        className={`catalog-search-suggestion ${isAutoSuggestion ? 'active' : ''}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleProgrammeCodeSuggestionSelect(suggestion)}
                      >
                        <span className="catalog-search-suggestion-label">{suggestion.code}</span>
                        <span className="catalog-search-suggestion-meta">Programme Code</span>
                      </button>
                    );
                  })}
                  {personSuggestions.map((suggestion) => {
                    const isAutoSuggestion =
                      autoPersonSearch?.role === suggestion.role
                      && autoPersonSearch?.normalizedName === suggestion.normalizedName;

                    return (
                      <button
                        key={`${suggestion.role}-${suggestion.normalizedName}`}
                        type="button"
                        className={`catalog-search-suggestion ${isAutoSuggestion ? 'active' : ''}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handlePersonSuggestionSelect(suggestion)}
                      >
                        <span className="catalog-search-suggestion-label">{suggestion.name}</span>
                        <span className="catalog-search-suggestion-meta">
                          {PERSON_ROLE_LABELS[suggestion.role] || 'Faculty'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <CustomDropdown
            label="Discipline"
            value={filters.discipline}
            onChange={(value) => handleFilterChange('discipline', value)}
            options={[
              { value: '', label: 'All Disciplines' },
              ...disciplines.map(d => ({ value: d, label: d }))
            ]}
          />
          <CustomDropdown
            label="Format"
            value={filters.format}
            onChange={(value) => handleFilterChange('format', value)}
            options={[
              { value: '', label: 'All Formats' },
              { value: 'In-person', label: 'In-person' },
              { value: 'Online', label: 'Online' },
              { value: 'Blended', label: 'Blended' }
            ]}
          />
          <CustomDropdown
            label="Sort"
            value={sortBy}
            onChange={setSortBy}
            options={[
              { value: 'discipline-asc', label: 'Discipline (A-Z)' },
              { value: 'discipline-desc', label: 'Discipline (Z-A)' },
              { value: 'code-asc', label: 'Code (A-Z)' },
              { value: 'code-desc', label: 'Code (Z-A)' },
              { value: 'title-asc', label: 'Title (A-Z)' },
              { value: 'title-desc', label: 'Title (Z-A)' },
              { value: 'faculty-asc', label: 'Faculty (A-Z)' },
              { value: 'faculty-desc', label: 'Faculty (Z-A)' },
              { value: 'format-asc', label: 'Format (A-Z)' },
              { value: 'format-desc', label: 'Format (Z-A)' },
              { value: 'location-custom', label: 'Location (London-Dubai-Riyadh)' },
              { value: 'duration-short', label: 'Duration (shortest)' },
              { value: 'duration-long', label: 'Duration (longest)' },

            ]}
          />
        </div>

        <div className="catalogue-filter-sub">
          <div className="catalogue-quick-filters">
            <span className="catalogue-quick-label"><Filter size={14} /> Quick Filter</span>
            {disciplines.map((discipline) => {
              const isActive = filters.discipline === discipline;

              return (
                <button
                  key={discipline}
                  type="button"
                  className={`catalogue-quick-chip ${isActive ? 'active' : ''}`}
                  onClick={() => handleFilterChange('discipline', isActive ? '' : discipline)}
                >
                  <span className="catalogue-quick-dot" style={{ background: getDisciplineAccent(discipline) }} />
                  {discipline}
                </button>
              );
            })}
            {hasNonEligibleCourses && (
              <button
                type="button"
                className={`catalogue-quick-chip ${filters.certificationEligible === 'yes' ? 'active' : ''}`}
                onClick={() => handleFilterChange('certificationEligible', filters.certificationEligible === 'yes' ? '' : 'yes')}
              >
                Certificate Eligible
              </button>
            )}
            {hasActiveFilters && (
              <button type="button" className="catalogue-clear-btn" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          <div className="catalogue-view-tools">
            <span className="catalogue-count">{filteredCourses.length} programme{filteredCourses.length !== 1 ? 's' : ''}</span>
            <div className="catalogue-view-toggle" role="group" aria-label="Catalogue view">
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="active-filters-row catalogue-active-filters">
            {query.trim() && (
              <button
                type="button"
                className="active-filter-chip"
                onClick={() => {
                  setQuery('');
                  setPersonSearch(null);
                  setProgrammeCodeSearch(null);
                }}
                aria-label={`Remove search filter: ${query.trim()}`}
              >
                {activeProgrammeCodeSearch
                  ? `Programme Code: ${activeProgrammeCodeSearch.code}`
                  : activePersonSearch
                  ? `Searching ${PERSON_ROLE_LABELS[activePersonSearch.role] || 'Faculty'}: ${activePersonSearch.name}`
                  : `Search: ${query.trim()}`}
                <X size={12} />
              </button>
            )}
            {filters.discipline && (
              <button type="button" className="active-filter-chip" onClick={() => handleFilterChange('discipline', '')} aria-label={`Remove discipline filter: ${filters.discipline}`}>
                Discipline: {filters.discipline}
                <X size={12} />
              </button>
            )}
            {filters.format && (
              <button type="button" className="active-filter-chip" onClick={() => handleFilterChange('format', '')} aria-label={`Remove format filter: ${filters.format}`}>
                Format: {filters.format}
                <X size={12} />
              </button>
            )}
            {hasNonEligibleCourses && filters.certificationEligible && (
              <button type="button" className="active-filter-chip" onClick={() => handleFilterChange('certificationEligible', '')} aria-label="Remove certificate eligible filter">
                Certificate Eligible
                <X size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      {filteredCourses.length === 0 && (
        <div className="card catalogue-empty-state">
          <h3>No programmes match your filters</h3>
          <p>Try broadening your search terms or clearing filters to see more options.</p>
          <button className="btn-primary" onClick={clearFilters}>Reset catalogue view</button>
        </div>
      )}

      {viewMode === 'list' && filteredCourses.length > 0 ? (
        <div className="programme-list-view">
          <div className="programme-list-header" role="row">
            {renderListHeader('discipline', 'Discipline')}
            {renderListHeader('code', 'Code', 'programme-list-code')}
            {renderListHeader('title', 'Programme', 'programme-list-title')}
            {renderListHeader('faculty', 'Faculty', 'programme-list-faculty')}
            {renderListHeader('format', 'Format')}
            {renderListHeader('location', 'Location')}
            {renderListHeader('duration', 'Duration')}

            <span className="programme-list-head-cell programme-list-actions">Actions</span>
          </div>
          {filteredCourses.map((course) => {
            const accent = getDisciplineAccent(course.discipline);
            return (
              <article key={course.id} className="programme-list-row" style={{ '--accent': accent }}>
                <span className="programme-list-disc"><span />{course.discipline}</span>
                <span className="programme-list-code">{course.programmeCode || '-'}</span>
                <span className="programme-list-title">{course.title}</span>
                <span className="programme-list-cell programme-list-faculty">{course.instructor || 'Faculty TBC'}</span>
                <span className="programme-list-cell">{course.format || 'TBC'}</span>
                <span className="programme-list-cell">{course.location || 'TBC'}</span>
                <span className="programme-list-cell">{course.duration || 'TBC'}</span>

                <span className="programme-list-actions">
                  <button type="button" className="programme-icon-btn" onClick={() => openCourseModal(course)} aria-label={`Show details for ${course.title}`}>
                    <Info size={14} />
                  </button>
                  <a className="programme-icon-btn" href={course.sourceUrl} target="_blank" rel="noreferrer" aria-label={`Open official page for ${course.title}`}>
                    <ExternalLink size={13} />
                  </a>
                </span>
              </article>
            );
          })}
        </div>
      ) : (
        filteredCourses.length > 0 && (
          <div className="program-grid programme-card-grid">
            {filteredCourses.map(course => {
              const accent = getDisciplineAccent(course.discipline);
              const summary = buildCardSummary(course);

              return (
                <article key={course.id} className="card program-card programme-card-v2" style={{ '--accent': accent }}>
                  <header className="programme-card-head">
                    <span className="programme-card-disc"><span />{course.discipline}</span>
                    <span className="programme-card-actions">
                      <button type="button" className="programme-icon-btn" onClick={() => openCourseModal(course)} aria-label={`Show details for ${course.title}`} title="Show details">
                        <Info size={14} />
                      </button>
                      <a className="programme-icon-btn" href={course.sourceUrl} target="_blank" rel="noreferrer" aria-label={`Open official page for ${course.title}`} title="Official page">
                        <ExternalLink size={13} />
                      </a>
                    </span>
                  </header>

                  <h3 className="programme-card-title">
                    {course.programmeCode && <span>{course.programmeCode}: </span>}
                    {course.title}
                  </h3>

                  {summary && <p className="programme-card-summary">{summary}</p>}

                  <div className="programme-card-meta">
                    <div><Users size={14} /><span>{course.instructor || 'Faculty TBC'}</span></div>
                    <div><MapPin size={14} /><span>{course.format || 'TBC'} - {course.location || 'Location TBC'}</span></div>
                    <div><Clock3 size={14} /><span>{course.duration || 'TBC'}</span></div>

                  </div>

                  <div className="programme-card-tags">
                    {hasNonEligibleCourses && course.certificationEligible && (
                      <span className="programme-card-tag">Certificate Eligible</span>
                    )}
                    {(course.keyTopics || []).filter((topic) => String(topic || '').trim().length <= 80).slice(0, 3).map((topic, i) => (
                      <span key={i} className="programme-card-tag">{topic}</span>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        )
      )}

      {selectedCourse && createPortal((
        <div className="modal-overlay" onClick={closeCourseModal}>
          <div
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            tabIndex="-1"
            ref={modalDialogRef}
          >
            <div className="modal-header">
              <div>
                <h2 className="modal-title" id={modalTitleId}>{selectedCourse.title}</h2>
                {selectedCourse.programmeCode && (
                  <div className="status-row" style={{ marginTop: '6px' }}>
                    <span className="tag keyword">Code: {selectedCourse.programmeCode}</span>
                    {selectedCourse.keyDetails?.averageExperience && (
                      <span className="tag keyword">Management experience: {selectedCourse.keyDetails.averageExperience}</span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeCourseModal}
                aria-label="Close details"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-content">
              <div className="program-section">
                <div className="section-label">Overview</div>
                <p className="program-detail-text">{selectedCourse.description}</p>
              </div>

              <div className="program-section">
                <div className="section-label">Target Audience</div>
                <p className="program-detail-text">{selectedCourse.targetAudience}</p>
              </div>

              <div className="program-section">
                <div className="section-label">Faculty</div>
                <p className="program-detail-text">{selectedCourse.instructor}</p>
              </div>

              {selectedCourse.cimCategory && (
                <div className="program-section">
                  <div className="section-label">Programme Pillar</div>
                  <p className="program-detail-text">{selectedCourse.cimCategory}</p>
                </div>
              )}

              <div className="program-section">
                <div className="section-label">All Key Topics</div>
                <div className="tag-list">
                  {(selectedCourse.keyTopics || []).map((topic, i) => (
                    <span key={i} className="tag keyword">{topic}</span>
                  ))}
                </div>
              </div>

              <div className="program-section">
                <div className="section-label">Learning Outcomes</div>
                <p className="program-detail-text">{selectedCourse.outcomes}</p>
              </div>

              {selectedCourse.contentDetails && Object.keys(selectedCourse.contentDetails).length > 0 && (
                <div className="program-section">
                  <div className="section-label">Programme Detail</div>
                  {selectedCourse.contentDetails.participantProfile && <p className="program-detail-text"><strong>Participant profile:</strong> {selectedCourse.contentDetails.participantProfile}</p>}
                  {selectedCourse.contentDetails.prerequisites && <p className="program-detail-text"><strong>Prerequisites:</strong> {selectedCourse.contentDetails.prerequisites}</p>}
                  {selectedCourse.contentDetails.sectorFocus && <p className="program-detail-text"><strong>Sector and industry focus:</strong> {selectedCourse.contentDetails.sectorFocus}</p>}
                  {selectedCourse.contentDetails.timetable && <p className="program-detail-text"><strong>Timetable:</strong> {selectedCourse.contentDetails.timetable}</p>}
                  {selectedCourse.contentDetails.sessionOverview && <p className="program-detail-text"><strong>Session overview:</strong> {selectedCourse.contentDetails.sessionOverview}</p>}
                  {selectedCourse.contentDetails.facultyContentSummary && <p className="program-detail-text"><strong>Faculty content summary:</strong> {selectedCourse.contentDetails.facultyContentSummary}</p>}
                  {selectedCourse.contentDetails.intendedImpact && <p className="program-detail-text"><strong>Intended impact:</strong> {selectedCourse.contentDetails.intendedImpact}</p>}
                  {selectedCourse.contentDetails.organisationalOutcomes && <p className="program-detail-text"><strong>Organisational outcomes:</strong> {selectedCourse.contentDetails.organisationalOutcomes}</p>}
                </div>
              )}

              {selectedCourse.keyDetails && (
                <div className="program-section">
                  <div className="section-label">Key Details & Participant Profile</div>
                  {(selectedCourse.keyDetails.averageExperience
                    || selectedCourse.keyDetails.academicDirector
                    || selectedCourse.keyDetails.programmeDirector) && (
                    <div className="key-details-grid">
                      {selectedCourse.keyDetails.averageExperience && (
                        <div className="key-detail-item">
                          <span className="detail-label">Management Experience</span>
                          <span className="detail-value">{selectedCourse.keyDetails.averageExperience}</span>
                        </div>
                      )}
                      {selectedCourse.keyDetails.academicDirector && (
                        <div className="key-detail-item">
                          <span className="detail-label">Academic Director</span>
                          <span className="detail-value">{selectedCourse.keyDetails.academicDirector}</span>
                        </div>
                      )}
                      {selectedCourse.keyDetails.programmeDirector && (
                        <div className="key-detail-item">
                          <span className="detail-label">Programme Director</span>
                          <span className="detail-value">{selectedCourse.keyDetails.programmeDirector}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(selectedCourse.testimonials) && selectedCourse.testimonials.length > 0 && (
                <div className="program-section">
                  <div className="section-label">Participant Testimonials</div>
                  <div className="program-detail-text">
                    {selectedCourse.testimonials.slice(0, 3).map((testimonial, quoteIndex) => {
                      const quote = typeof testimonial === 'string' ? testimonial : testimonial?.quote;
                      const name = typeof testimonial === 'object' ? testimonial?.name : '';
                      const designation = typeof testimonial === 'object' ? testimonial?.designation : '';
                      const attribution = [name, designation].filter(Boolean).join(', ');

                      return (
                        <p key={`${selectedCourse.id}-testimonial-${quoteIndex}`}>
                          "{quote}"
                          {attribution ? <><br /><strong>- {attribution}</strong></> : null}
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </section>
  );
};

export default CourseList;
